import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/Alert";
import { AlertCircle } from "lucide-react";
import AccessCodeInput from "../../components/AccessCodeInput";

// Components
import SubscriptionHeader from "./components/SubscriptionHeader";
import CurrentSubscription from "./components/CurrentSubscription";
import PlanTabs from "./components/PlanTabs";
import ComparisonTable from "./components/ComparisonTable";
import PlanRecommendation from "./components/PlanRecommendation";

// Types & Data
import { EnhancedPlan } from "./types";
import { featureCategories } from "./data/planFeatures";
import { usePayPalPlans } from "src/hooks/useFetchPaypalPlan";

const SubscriptionPage: React.FC = () => {
    const {
        firebaseUser,
        subscription,
        loading: authLoading,
        subscriptionPlan,
        checkSubscription,
        hasActiveSubscription,
    } = useAuth();

    // const [plans, setPlans] = useState<EnhancedPlan[]>([]);
    const [selectedPlan, setSelectedPlan] = useState<string>("");
    // const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
    const [recommendedPlan, setRecommendedPlan] = useState<
        "basic" | "plus" | "pro"
    >("basic");
    
    // Access code state
    const [accessCode, setAccessCode] = useState<string>('');
    const [discountAmount, setDiscountAmount] = useState<number>(0);

    // Set current plan ID - use subscription plan ID if active, otherwise use free plan
    useEffect(() => {
        if (hasActiveSubscription && subscriptionPlan) {
            setCurrentPlanId(subscriptionPlan.id);
        } else {
            setCurrentPlanId("free-plan");
        }
    }, [hasActiveSubscription, subscriptionPlan]);

    // PayPal configuration options
    const paypalOptions = {
        clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID,
        currency: "USD",
        intent: "subscription",
        components: "buttons",
        // "disable-funding": "credit,card", // Note the dash-case format
        vault: true,
    };
    const {
        data: plans = [],
        isLoading: loading,
        isError,
        error: fetchError,
    } = usePayPalPlans({
        isAuthenticated: !authLoading && Boolean(firebaseUser),
    });
    useEffect(() => {
        if (plans.length > 0) {
            if (subscriptionPlan) {
                setSelectedPlan(subscriptionPlan.id);
            } else {
                setSelectedPlan(plans[0].id); // Free plan is first in the array
            }
        }
    }, [plans, subscriptionPlan]);

    const handleRecommendationChange = (plan: "basic" | "plus" | "pro") => {
        setRecommendedPlan(plan);
        // Find the corresponding plan ID and set it as selected
        const planId = plans.find((p) =>
            plan === "basic"
                ? p.price === 0
                : plan === "plus"
                ? p.name.toLowerCase().includes("plus")
                : p.name.toLowerCase().includes("pro")
        )?.id;
        if (planId) {
            setSelectedPlan(planId);
        }
    };

    // Handle access code validation
    const handleValidAccessCode = (code: string, discount: number) => {
        console.log("=== Access Code Applied on Subscription Page ===");
        console.log("Code:", code);
        console.log("Discount:", discount);
        setAccessCode(code);
        setDiscountAmount(discount);
        setError(null);
    };
    
    const handleInvalidAccessCode = () => {
        console.log("=== Access Code Removed on Subscription Page ===");
        setAccessCode('');
        setDiscountAmount(0);
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <div className="container mx-auto px-4 py-8 max-w-7xl">
                {/* Page Header */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">
                        Choose Your Plan
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                        Select the perfect plan for your interview preparation journey
                    </p>
                </div>

                {/* Current subscription status */}
                {hasActiveSubscription && (
                    <CurrentSubscription
                        subscription={subscription}
                        subscriptionPlan={subscriptionPlan}
                        hasActiveSubscription={hasActiveSubscription}
                    />
                )}

                {/* Step 1: Access Code (for new users) */}
                {!hasActiveSubscription && (
                    <div className="mb-8">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                                1
                            </div>
                            <h2 className="text-lg font-semibold text-gray-800">
                                Have a discount code? (Optional)
                            </h2>
                        </div>
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <AccessCodeInput
                                onValidCode={handleValidAccessCode}
                                onInvalidCode={handleInvalidAccessCode}
                                disabled={loading}
                            />
                            
                            {accessCode && discountAmount > 0 && (
                                <div className="mt-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-300 rounded-lg p-4 flex items-center gap-3">
                                    <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <div>
                                        <p className="text-green-800 font-semibold">
                                            ${discountAmount} discount activated!
                                        </p>
                                        <p className="text-green-700 text-sm">
                                            Applied to all plans below for your first month
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Error alert */}
                {error && (
                    <Alert variant="destructive" className="mb-6">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Error</AlertTitle>
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {/* Step 2: Choose Your Plan */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-6">
                        <div className={`w-8 h-8 ${!hasActiveSubscription ? 'bg-blue-500' : 'bg-gray-500'} text-white rounded-full flex items-center justify-center font-bold text-sm`}>
                            {!hasActiveSubscription ? '2' : '1'}
                        </div>
                        <h2 className="text-lg font-semibold text-gray-800">
                            Choose your plan
                        </h2>
                    </div>
                    
                    {/* Plan recommendation for new users */}
                    {!hasActiveSubscription && (
                        <div className="mb-6">
                            <PlanRecommendation
                                onRecommendationChange={handleRecommendationChange}
                            />
                        </div>
                    )}

                    <PlanTabs
                        plans={plans}
                        loading={loading}
                        currentPlanId={currentPlanId}
                        hasActiveSubscription={hasActiveSubscription}
                        currentSubscriptionName={subscriptionPlan?.name}
                        selectedPlan={selectedPlan}
                        onPlanSelect={setSelectedPlan}
                        recommendedPlan={recommendedPlan}
                        accessCode={accessCode}
                        discountAmount={discountAmount}
                    />
                </div>

                {/* Step 3: Compare Features */}
                {!loading && (
                    <div className="mb-8">
                        <div className="flex items-center gap-3 mb-6">
                            <div className={`w-8 h-8 ${!hasActiveSubscription ? 'bg-blue-500' : 'bg-gray-500'} text-white rounded-full flex items-center justify-center font-bold text-sm`}>
                                {!hasActiveSubscription ? '3' : '2'}
                            </div>
                            <h2 className="text-lg font-semibold text-gray-800">
                                Compare features
                            </h2>
                        </div>
                        <ComparisonTable featureCategories={featureCategories} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubscriptionPage;
